'use client';

import { useEffect } from 'react';
import { capture } from '@/config/captureUserEvent';
import { EventNames } from '@/config/eventNames';

/** "Can I pay weekly or per lesson?" -> "can-i-pay-weekly-or-per-lesson" */
function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Records which FAQ answers people open, so "what do visitors ask before
 * booking" has a direct answer instead of an autocapture guess. Autocapture
 * cannot tell an open from a close, and a tap on the arrow icon arrives with no
 * question text at all.
 *
 * Listens on the section in the capture phase: the answer panel is still
 * `hidden` at that point if this tap is opening it. Programmatic clicks
 * (OpenFaqOnHash opening #pricing) are not a person asking, so only trusted
 * clicks count.
 */
export function TrackFaqOpens({ sectionId }: { sectionId: string }) {
  useEffect(() => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    const onClick = (event: MouseEvent) => {
      if (!event.isTrusted) return;
      const title = (event.target as Element | null)?.closest('button');
      if (!title || !section.contains(title)) return;
      if (!title.nextElementSibling?.hasAttribute('hidden')) return;

      const titles = Array.from(section.querySelectorAll('button[type="button"]'));
      const question = title.textContent?.trim() ?? '';
      capture(EventNames.faqOpened, {
        // The price question's wording changes per instrument; its id does not.
        question_id: title.id || slug(question),
        question,
        position: titles.indexOf(title) + 1,
      });
    };

    section.addEventListener('click', onClick, true);
    return () => section.removeEventListener('click', onClick, true);
  }, [sectionId]);

  return null;
}
